import { BryntumGrid } from "@bryntum/schedulerpro-react";
import React, { useContext, useEffect, useMemo, useRef } from "react";
import { REMINDERS_GRID_CONFIG } from "../../other/constants/ReminderViewGridConfig.js";
import useRefsInit from "../hooks/useRefsInit.jsx";
import AuthContext from "../context/authContext.jsx";
import BryntumContext from "../context/bryntumContext.jsx";
import {
  cellAvatarTooltipConfig,
  defaultCellRenderer,
  getOriginalRecord,
  swivuCheckGridGrants
} from "../../other/utils/utils.jsx";
import { GRANTS } from "../../other/constants/PrivilegeTypes.jsx";
import { callOnLoadOnColumns, canOpenEditor, pluralize } from "../../other/utils/otherUtils.jsx";
import {
  cellMenuItemMarkRead,
  cellMenuItemMarkUnread,
  cellMenuItemRemoveRow,
  cellMenuItemToggleArchiveRow,
  handleOnRecordDelete,
  performOperationWithHandlingRefreshes,
  processCellMenuItems
} from "../../other/utils/gridCellMenuUtils.jsx";
import {
  ALERT_ENTITIES,
  ALERT_TYPES,
  GRID_FIELDS_TO_OPEN_EDITOR,
  NOTIFICATION_STATUS,
  VIEW_TYPES
} from "../../other/constants/OtherKeys.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import { DateHelper } from "@bryntum/schedulerpro";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import useViews from "../hooks/useViews.jsx";
import ViewSpecificHeader from "../components/appHeader/ViewSpecificHeader.jsx";

const filterBoardAlerts = (reminderStore) => {
  reminderStore.sort({
    field: "id",
    ascending: false,
    hidden: true, // we don't want to persist with view state
  });
  reminderStore.group({
    field: "reminderRecipientValues",
    ascending: true,
    hidden: true, // we don't want to persist with view state
  });
  reminderStore.filter({
    filterBy(record) {
      return record.status === NOTIFICATION_STATUS.SENT && DateHelper.isBefore(new Date(record.alertDate), new Date()); // retrieve all records except the future ones
    },
    hidden: true, // we don't want to persist with view state
  });
}

const filterMyAlerts = (reminderStore, boardUserRecordId) => {
  reminderStore.filter({
    filterBy(record) {
      return record.status === NOTIFICATION_STATUS.SENT && Boolean(record.recipients.find((recipient) => recipient.boardUserId === boardUserRecordId))
    },
    hidden: true, // we don't want to persist with view state
  });
}

const filterRecentAlerts = (reminderStore) => {
  reminderStore.sort({
    field: "_alertDate",
    ascending: false,
    hidden: true, // we don't want to persist with view state
  });
  reminderStore.filter({
    filterBy(record) {
      // we need to display recent alerts of only the last 5 days
      return record.status === NOTIFICATION_STATUS.SENT && DateHelper.isAfter(new Date(record.alertDate), DateHelper.add(new Date(), -5, "days"))
    },
    hidden: true, // we don't want to persist with view state
  });
}

const clearAllFilters = (reminderStore) => {
  reminderStore.clearFilters();
  reminderStore.clearSorters();
  reminderStore.clearGroupers();
  reminderStore.swivuArchiveStateChange();
}

let timeoutClear;

const AlertPage = () => {
  const gridRef = useRef(null);
  const {setRefs, isRefsInit} = useRefsInit([gridRef]);
  const {user} = useContext(AuthContext);
  const context  = useContext(BryntumContext);
  const {
    project,
    editorHandler,
    selectedBoard,
    entityManager
  } = context;
  const viewManagerState = useViews();
  const isDefaultViewState = useMemo(() => viewManagerState.selectedView.isDefaultView, [viewManagerState.selectedView]);
  useEnableViewTracking(gridRef, VIEW_TYPES.notify, isRefsInit);

  useEffect(() => {
    if (project && user) {
      gridRef.current.instance.store = project.reminderStore;
      timeoutClear = setTimeout(() => {
        // don't know why but this doesn't work if we don't add delay
        filterRecentAlerts(project.reminderStore);
        if (gridRef.current) gridRef.current.instance.selectedRecords = [];
      }, 0);
      gridRef.current.instance.store.relayAll(gridRef.current.instance, "swivuAllReminderStore");
      swivuCheckGridGrants(gridRef.current.instance, user);
      callOnLoadOnColumns(gridRef.current.instance.columns, project);
      gridRef.current.instance.features.cellMenu.processItems = processCellMenuItems({
        entityName: "Notification", user, grid: gridRef.current.instance
      });
    }
    // viewManagerState is not used in this effect, but we need to rerender if the group tab is selected then select the view, the useEffect didn't run.

    return () => {
      clearTimeout(timeoutClear);
    }
  }, [gridRef, project, user, viewManagerState]);

  // this useEffect handling tbar for grid. the tbar only appears in non-custom view
  useEffect(() => {
    const alertGridInstance = gridRef.current.instance;
    alertGridInstance.tbar = isDefaultViewState ? [
      {
        ref: "alertFilterButtons",
        type: 'buttongroup',
        toggleGroup: true,
        cls: "!flex !gap-[12px] items-center",
        rendition: "padded",
        items: {
          filterMyAlertsBtn: {
            type: "button",
            text: defaultCellRenderer({ value: user?.first_name}),
            minWidth: "10em",
            minHeight: "auto",
            height: "2.5em",
          },
          filterBoardAlertsBtn: {
            type: "button",
            text: defaultCellRenderer({value: selectedBoard.name}),
            minWidth: "10em",
            minHeight: "auto",
            height: "2.5em",
          },
          filterRecentAlertsBtn: {
            type: "button",
            text: "Recent",
            minWidth: "7em",
            minHeight: "auto",
            height: "2.5em",
            pressed: true
          },
        }
      },
      {
        ref: "alertContextMenu",
        type: "button",
        minWidth: "1em",
        minHeight: "auto",
        height: "2.5em",
        cls: "!rounded-md dark:text-[#c0c1c2] border-0 bg-[#e7e3e3] dark:bg-[#3d3d41] self-center",
        menuIcon: "fa-light fa-ellipsis !m-0",
        menu: {
          items: {
            markRead: cellMenuItemMarkRead({ weight: 1 }),
            markUnread: cellMenuItemMarkUnread({ weight: 2 }),
            removeRow: cellMenuItemRemoveRow({ weight: 3 }),
            archive: cellMenuItemToggleArchiveRow({ weight: 4 }),
            unArchive: cellMenuItemToggleArchiveRow({ weight: 5 }),
          },
        },
        listeners: {
          beforeShowMenu({ menu }) {
            // Todo: Use processCellItems from grid cell menu as it handles logics for menu items
            const { markRead, markUnread, archive, removeRow, unArchive } = menu.widgetMap;
            const selectedItems = alertGridInstance.selectedRecords;
            const isMultiValue = alertGridInstance.selectedRecords.length > 1;
            const countText = `${selectedItems.length} ${pluralize(selectedItems.length, "Notification")}`;
            const isEveryRecordArchive = selectedItems.every((e) => e.archived);
            const isEveryRecordUnArchive = selectedItems.every((e) => !e.archived);
            const isEveryRecordSeen = selectedItems.every((e) => e.isSeen);
            const isEveryRecordUnSeen = selectedItems.every((e) => !e.isSeen);
            archive.hidden = isEveryRecordArchive;
            unArchive.hidden = isEveryRecordUnArchive;
            removeRow.hidden = !isEveryRecordArchive;
            markRead.hidden = isEveryRecordSeen;
            markUnread.hidden = isEveryRecordUnSeen;
            if (isMultiValue) {
              markRead.text = `Mark ${countText} as Read`;
              markUnread.text = `Mark ${countText} as Unread`;
              archive.text = `Archive ${countText}`;
              unArchive.text = `Unarchive ${countText}`;
              removeRow.text = `Delete ${countText}`
            } else {
              markRead.text = "Mark as Read";
              markUnread.text = "Mark as Unread";
              archive.text = "Archive";
              unArchive.text = "Unarchive";
              removeRow.text = "Delete"
            }
            const gridInstance = gridRef.current.instance
            markRead.onItem = () => {
              performOperationWithHandlingRefreshes(gridInstance, () => {
                selectedItems.forEach((record) => {
                  record.updateSeenStatus("markSeen");
                })
              })();
            }
            markUnread.onItem = () => {
              performOperationWithHandlingRefreshes(gridInstance, () => {
                selectedItems.forEach((record) => {
                  record.updateSeenStatus("markUnseen");
                })
              })();
            }
            archive.onItem = () => {
              performOperationWithHandlingRefreshes(gridInstance, () => {
                selectedItems.forEach((record) => {
                  record.archived = true;
                })
              })();
            }
            unArchive.onItem = () => {
              performOperationWithHandlingRefreshes(gridInstance, () => {
                selectedItems.forEach((record) => {
                  record.archived = false;
                })
              })();
            }
            removeRow.onItem = handleOnRecordDelete(selectedItems, alertGridInstance.store);
          }
        },
        hidden: true,
      }
    ] : undefined;
    const tbarButtonGroup = alertGridInstance.tbar?.widgetMap?.alertFilterButtons;
    if (alertGridInstance && isDefaultViewState && tbarButtonGroup) {
      if (tbarButtonGroup) {
        tbarButtonGroup.on("click", (e) => {
          alertGridInstance.selectedRecords = [];
          clearAllFilters(project.reminderStore);
          if (e.source.ref === "filterMyAlertsBtn") {
            const boardUserRecord = project.boardUsersStore.findRecord("userId", user.id, true);
            filterMyAlerts(project.reminderStore, boardUserRecord.id);
          } else if (e.source.ref === "filterBoardAlertsBtn") {
            filterBoardAlerts(project.reminderStore);
          } else if (e.source.ref === "filterRecentAlertsBtn") {
            filterRecentAlerts(project.reminderStore);
          }
        }, alertGridInstance);
      }
    }
    return () => {
      if (project.reminderStore && isDefaultViewState) {
        // need to clear all filters from reminderStore before switch to another view, not doing so will result banners to not appear correctly.
        clearAllFilters(project.reminderStore);
      }
    }
  }, [project, isDefaultViewState, viewManagerState]);

  const onOpenEditorByEntityType = ({record, column}) => {
    record = getOriginalRecord(record);
    if (canOpenEditor(column, record, [GRID_FIELDS_TO_OPEN_EDITOR.ENTITY_NAME])) {
      const editor = ALERT_ENTITIES[record.entityType];
      if (editor) {
        entityManager.openEntityManager({ entityType: editor.entityManagerPage, entityId: record.entity.id });
      } else if (!editor && record.alertType === ALERT_TYPES.BoardAlert) {
        editorHandler.openEditor(EDITOR_KEYS.boardAlertEditor, record, { reminderRecord: record });
      }
    }
  }

  const cellClickHandler = ({event, column, record}) => {
    if (!record.isGroupHeader && column.isAlertBellColumn && user && user.can(GRANTS.grant_four)) {
      record.updateSeenStatus();
    } else if (column.id === GRID_FIELDS_TO_OPEN_EDITOR.RECIPIENT_NAME && canOpenEditor(column, record)) {
      const idKey = "swivu-recipient-editor-button";
      const target = event.target;
      const { id }  = (target.id === idKey ? target.dataset : target.closest(`#${idKey}`)?.dataset) ?? {};
      if (id) {
        const user = project.boardUsersStore.getById(id);
        if (user) {
          editorHandler.openEditor(EDITOR_KEYS.userEditor, user, { user: user });
        }
      }
    }
  }

  const onAlertSelectionChange = ({ selection, source: alertGridInstance }) => {
    const { alertContextMenu } = alertGridInstance.widgetMap;
    if (alertContextMenu) {
      alertContextMenu.hidden = !selection.length;
    }
  }

  return (
    <>
      <ViewSpecificHeader />
      <BryntumGrid
        ref={setRefs(gridRef)}
        {...REMINDERS_GRID_CONFIG}
        disabled={user && !user.can(GRANTS.grant_four)}
        readOnly={user && !user.can(GRANTS.grant_four)}
        onCellClick={cellClickHandler}
        onCellDblClick={onOpenEditorByEntityType}
        preserveScrollOnDatasetChange
        cls="swivu-notify-grid-container"
        onSelectionChange={onAlertSelectionChange}
        cellTooltipFeature={cellAvatarTooltipConfig}
        searchFeature={true}
      />
    </>
  );
};

export default AlertPage;
